import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import coachAvailabilityService, {
  CoachAvailabilityDto
} from '../../services/coachAvailabilityService';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { formatServerDateTime } from '../../utils/dateUtils';

interface CoachAvailabilityDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: (availabilityId: string) => void;
  onDelete: () => void;
  availabilityId: string | null;
}

const CoachAvailabilityDetailsDialog: React.FC<CoachAvailabilityDetailsDialogProps> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  availabilityId
}) => {
  const [availability, setAvailability] = useState<CoachAvailabilityDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();

  useEffect(() => {
    if (open && availabilityId) {
      loadAvailabilityData();
    }

    // Get user info from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // A JWT payload-ban a claim-kulcsok a TELJES .NET URI-k (…/claims/role, …/nameidentifier),
        // nem „role"/„nameid" — ezért a korábbi olvasás mindig üres volt, és SENKI nem látott
        // szerkesztés/törlés gombot a részletezőben (előzetes hiba, 2026-09-12). A bejelentkezett
        // felhasználó a localStorage-ban tárolt, a belépéskor kapott `user` objektumból jön.
        const payload = JSON.parse(atob(token.split('.')[1]));
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        setUserRole(storedUser?.userRole || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || payload.role || '');
        setCurrentUserId(storedUser?.userId || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || payload.nameid || '');
      } catch (err) {
        console.error('Error parsing token:', err);
      }
    }
  }, [open, availabilityId]);

  const loadAvailabilityData = async () => {
    if (!availabilityId) return;

    try {
      setLoading(true);
      setError('');
      const data = await coachAvailabilityService.getCoachAvailability(availabilityId);
      setAvailability(data);
    } catch (err) {
      console.error('Error loading availability data:', err);
      setError(apiErrorMessage(err, 'Hiba történt az elérhetőség betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (availability) {
      onEdit(availability.availabilityId);
    }
  };

  const handleDelete = async () => {
    if (!availability) return;

    const confirmed = await confirm({
      title: 'Elérhetőség törlése',
      message: 'Biztosan törölni szeretné ezt az elérhetőséget?',
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setDeleting(true);
      await coachAvailabilityService.deleteCoachAvailability(availability.availabilityId);
      showSuccess('Az elérhetőség törölve.');
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Error deleting availability:', err);
      // [Web UX 3. csomag (D), 2026-10-04] a kétsoros `if (…?.message)` alak: az `else` tartaléka lett a fallback.
      setError(apiErrorMessage(err, 'Hiba történt az elérhetőség törlésekor'));
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Sorozat törlése INNENTŐL: ez a nap és a sorozat összes későbbi tagja. A korábbi, már megtörtént
   * napok maradnak. [UX 2026-09-12 — heti ismétlés]
   */
  const handleDeleteSeries = async () => {
    if (!availability?.seriesId) return;
    const confirmed = await confirm({
      title: 'Sorozat törlése',
      message: 'Törlöd a sorozat ezt és minden későbbi alkalmát? A korábbi napok megmaradnak.',
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;
    try {
      setDeleting(true);
      await coachAvailabilityService.deleteCoachAvailabilitySeries(availability.seriesId, availability.availabilityDate);
      showSuccess('A sorozat törölve.');
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Error deleting availability series:', err);
      setError(apiErrorMessage(err, 'Hiba történt a sorozat törlésekor'));
    } finally {
      setDeleting(false);
    }
  };

  const canEdit = () => {
    if (!availability) return false;
    return userRole === 'Administrator' ||
           (userRole === 'Coach' && availability.coachId === currentUserId);
  };

  const canDelete = () => {
    return canEdit();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatTime = (timeString: string): string => {
    return timeString.slice(0, 5);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Edző Elérhetőség Részletei</Typography>
        {availability && (
          <Box>
            {canEdit() && (
              <IconButton
                onClick={handleEdit}
                color="primary"
                // [Web UX 9 / T5, 2026-10-13] címke nélküli szerkesztő-gomb → aria-label.
                aria-label="Elérhetőség szerkesztése"
              >
                <EditIcon />
              </IconButton>
            )}
            {canDelete() && (
              <IconButton onClick={handleDelete} color="error" disabled={deleting} aria-label="Elérhetőség törlése" data-testid="availability-delete">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        )}
      </DialogTitle>
      {availability?.seriesId && canDelete() && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert
            severity="info"
            icon={<RepeatIcon fontSize="inherit" />}
            data-testid="availability-series-info"
            action={
              <Button color="error" size="small" onClick={handleDeleteSeries} disabled={deleting} data-testid="availability-delete-series">
                Sorozat törlése innentől
              </Button>
            }
          >
            Heti ismétlés része. A törlés csak ezt a napot érinti; a sorozat többi alkalmát innentől együtt is törölheted.
          </Alert>
        </Box>
      )}

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {availability && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Coach Information */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6">{availability.coachName}</Typography>
            </Box>

            <Divider />

            {/* Date and Time */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Időpont</Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: 4 }}>
                {formatDate(availability.availabilityDate)}
              </Typography>
              <Typography variant="body1" sx={{ ml: 4 }}>
                {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
              </Typography>
            </Box>

            {/* Venues */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">Helyszínek</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 4 }}>
                {availability.venues.map((venue) => (
                  <Chip
                    key={venue.venueId}
                    label={venue.name}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            {/* Notes */}
            {availability.notes && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <NotesIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">Megjegyzések</Typography>
                </Box>
                <Typography variant="body2" sx={{ ml: 4, fontStyle: 'italic' }}>
                  {availability.notes}
                </Typography>
              </Box>
            )}

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">Státusz:</Typography>
              <Chip
                label={availability.isActive ? 'Aktív' : 'Inaktív'}
                color={availability.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>

            <Divider />

            {/* Metadata */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                {/* [Web UX 1. csomag (4.3), 2026-10-03] UTC, „Z” nélkül (`CoachAvailabilityController.cs`) → közös segéd. */}
                Létrehozva: {formatServerDateTime(availability.createdDate, { fallback: '-' })}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Módosítva: {formatServerDateTime(availability.modifiedDate, { fallback: '-' })}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Bezárás
        </Button>
        {availability && canEdit() && (
          <Button
            onClick={handleEdit}
            variant="outlined"
            startIcon={<EditIcon />}
          >
            Szerkesztés
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CoachAvailabilityDetailsDialog;